// Phase 31 Plan 31-01 — ScorePill formatter (RUNE-30 / amendment §3.3 + §2.4).
//
// Pure function — produces the player-facing breakdown string the ScorePill
// component renders. Mirrors backend `apps/backend/src/match/economy/scoring.ts`
// cell_value formula for DISPLAY ONLY; server is authoritative on submitted
// turn rewards (DEC-021).
//
// Locked rules (T-31-05 — apps/web/scripts/verify-no-raw-tier-bonus.sh):
//   - NEVER emit raw +1 / +3 / +6 tier-bonus literals in template strings.
//     Tier labels come from intl.formatMessage (Forged/Rare/Mythic). Common
//     tier is OMITTED.
//   - Bonus labels (+Aff / +Anchor / +Poach) come from intl.formatMessage.
//   - +Anchor REPLACES +Poach on Anchor cells (never both).
//   - Cell value RHS "= N" is the integer cell-value display per RUNE-09 —
//     that's the legitimate numeric output; the grep gate is regex-aware
//     of template-literal `+N` patterns only.
//   - Bonus order per plan <behavior>: tier label → +Aff → +Anchor|+Poach.
//
// Cell value formula (mirrors scoring.ts computeCellValue):
//   value = 1                            (base)
//         + tier_bonus[cell.tier]        (common=0, forged=1, rare=3, mythic=6)
//         + anchor_bonus | poach_bonus   (anchor own=2, anchor opp=3, poach=1)
//         + affinity_bonus               (caller's affinity set contains letter = 1)

import type { IntlShape } from 'react-intl'
import { tokenBandFor } from '../../game/economy/tokenBands'
import type { PlayerView } from '../../game/PlayerViewSchema'

type Cell = PlayerView['board'][number]

/**
 * Tier bonus table — mirrors apps/backend/src/match/economy/defaults.ts
 * `TIER_BONUSES` Object.freeze'd const (Phase 23 lock). DOM source is the
 * canonical authority on the wire; this client mirror enables ScorePill
 * preview before TURN_APPLIED lands.
 */
const TIER_BONUS: Readonly<Record<Cell['tier'], number>> = Object.freeze({
  common: 0,
  forged: 1,
  rare: 3,
  mythic: 6,
})

const ANCHOR_BONUS_OWN = 2
const ANCHOR_BONUS_OPPONENT = 3
const POACH_BONUS = 1
const AFFINITY_BONUS = 1

export interface CellBreakdown {
  readonly letter: string
  readonly value: number
  readonly tierLabel: string | null
  readonly bonuses: ReadonlyArray<string>
}

/**
 * SCORE-06 — structured per-cell breakdown for the readable ScorePill layout.
 * One entry per traced letter cell; `labels` is the tier + bonus chips in
 * canon order. `formatScorePill` (the flat string) remains the SR aria-label.
 */
export interface ScorePillCellGroup {
  readonly key: string
  /**
   * Board cell index this group scores. ARCA-597 — the per-cell combat text
   * (WordTraceLayer) anchors each `+value` at this cell's center, so it needs
   * the source idx, not just the path position. Filled straight from the
   * traced `path` entry; never recomputed.
   */
  readonly cellIdx: number
  readonly letter: string
  readonly labels: ReadonlyArray<string>
  readonly value: number
}
export interface ScorePillBreakdown {
  readonly cells: ReadonlyArray<ScorePillCellGroup>
  readonly total: number
  readonly tokens: number
}

/**
 * SCORE-06 — build the structured breakdown the readable ScorePill renders as
 * discrete non-breaking per-cell groups + a separate Total/tokens line. Pure;
 * mirrors `formatScorePill`'s per-cell math (same `formatCell`) so the two can
 * never diverge.
 */
export function buildScorePillBreakdown(
  path: ReadonlyArray<number>,
  view: PlayerView,
  callerSlot: 0 | 1,
  intl: IntlShape,
): ScorePillBreakdown {
  const cellByIdx = new Map<number, Cell>()
  for (const c of view.board) cellByIdx.set(c.cell_idx, c)
  const affinitySet = new Set<string>(view.your_affinity_letters)

  const cells: ScorePillCellGroup[] = []
  let total = 0
  let i = 0
  for (const idx of path) {
    const cell = cellByIdx.get(idx)
    if (!cell) continue
    const b = formatCell(cell, callerSlot, affinitySet, intl)
    if (b.letter === '') continue
    const labels: string[] = []
    if (b.tierLabel) labels.push(b.tierLabel)
    for (const x of b.bonuses) labels.push(x)
    cells.push({
      key: `${idx}-${i}`,
      cellIdx: idx,
      letter: b.letter.toUpperCase(),
      labels,
      value: b.value,
    })
    total += b.value
    i += 1
  }
  return { cells, total, tokens: tokenBandFor(total) }
}

/**
 * Pure per-cell breakdown — looks up tier bonus + anchor/poach + affinity for
 * the `cell` from the caller's perspective and returns the structured
 * breakdown for downstream string composition.
 */
export function formatCell(
  cell: Cell,
  callerSlot: 0 | 1,
  affinitySet: ReadonlySet<string>,
  intl: IntlShape,
): CellBreakdown {
  const letter = cell.letter ?? ''
  // Hollow / null-letter cells have no display value — return a zero breakdown.
  if (letter === '' || cell.is_hollow) {
    return { letter: '', value: 0, tierLabel: null, bonuses: [] }
  }

  let value = 1 + TIER_BONUS[cell.tier]
  const bonuses: string[] = []

  // Tier label (Common is omitted).
  let tierLabel: string | null = null
  if (cell.tier === 'forged') {
    tierLabel = intl.formatMessage({ id: 'match.score_pill.tier_forged' })
  } else if (cell.tier === 'rare') {
    tierLabel = intl.formatMessage({ id: 'match.score_pill.tier_rare' })
  } else if (cell.tier === 'mythic') {
    tierLabel = intl.formatMessage({ id: 'match.score_pill.tier_mythic' })
  }

  // Affinity (stacks on everything — Phase 27 lock; amendment §2.4 + DEC-035).
  if (affinitySet.has(letter)) {
    value += AFFINITY_BONUS
    bonuses.push(intl.formatMessage({ id: 'match.score_pill.bonus_aff' }))
  }

  // Anchor REPLACES poach on Anchor cells (Phase 26 lock — Pitfall 7).
  if (cell.is_anchor) {
    value +=
      cell.anchor_owner === callerSlot
        ? ANCHOR_BONUS_OWN
        : ANCHOR_BONUS_OPPONENT
    bonuses.push(intl.formatMessage({ id: 'match.score_pill.bonus_anchor' }))
  } else if (cell.tint !== null) {
    const callerTint = callerSlot === 0 ? 'a' : 'b'
    if (cell.tint !== callerTint) {
      value += POACH_BONUS
      bonuses.push(intl.formatMessage({ id: 'match.score_pill.bonus_poach' }))
    }
  }

  return { letter, value, tierLabel, bonuses }
}

/**
 * Compose the player-facing breakdown string for the ScorePill given the
 * current trace `path`, caller's `view`, slot, and intl context.
 *
 * Returns the empty string when `path.length === 0` (empty-path safety; the
 * ScorePill component renders nothing in that case per UI-SPEC §4).
 *
 * Output shape (amendment §2.4 fixtures + UI-SPEC §7):
 *   "{letter}{ +tier_label}{ +bonus_labels} = {value} | ... | Total = {sum} → +{tokens} tokens"
 *
 * Note on i18n: per-cell template structure (letter + labels + " = " + value)
 * is composed with literal `=` (RUNE-30 is a tactical/numeric display, not
 * narrative prose — translations adjust the breakdown KEY only). The
 * aggregate row uses the `match.score_pill.breakdown` i18n key with
 * `{cells, score, tokens}` interpolation.
 */
export function formatScorePill(
  path: ReadonlyArray<number>,
  view: PlayerView,
  callerSlot: 0 | 1,
  intl: IntlShape,
): string {
  if (path.length === 0) return ''

  const cellByIdx = new Map<number, Cell>()
  for (const c of view.board) {
    cellByIdx.set(c.cell_idx, c)
  }
  const affinitySet = new Set<string>(view.your_affinity_letters)

  const breakdowns: string[] = []
  let total = 0
  for (const idx of path) {
    const cell = cellByIdx.get(idx)
    if (!cell) continue
    const b = formatCell(cell, callerSlot, affinitySet, intl)
    if (b.letter === '') continue
    const labels: string[] = []
    if (b.tierLabel) labels.push(b.tierLabel)
    for (const x of b.bonuses) labels.push(x)
    const lhs =
      labels.length === 0
        ? `${b.letter.toUpperCase()}`
        : `${b.letter.toUpperCase()} ${labels.join(' ')}`
    breakdowns.push(`${lhs} = ${b.value}`)
    total += b.value
  }

  const tokens = tokenBandFor(total)
  return intl.formatMessage(
    { id: 'match.score_pill.breakdown' },
    {
      cells: breakdowns.join(' | '),
      score: total,
      tokens,
    },
  )
}
